import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {environment} from "../../environments/environment";
import {NavigationEnd, Router} from "@angular/router";
import {HomeService} from "../home/home.service";
import {TokenStorageService} from "../_services/token-storage.service";
import {Globals} from "../globals";
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import {Observable, of, Subject, forkJoin, Subscription} from "rxjs";
import {BreadcrumbService} from "xng-breadcrumb";
import {itemUrlSegment} from '../shared/item-url.utils';
import {resolveProfileId} from '../shared/listing-route.utils';
import {buildAddListingBreadcrumbTrail} from '../shared/listing-route.utils';
import {isKnownBaseCategory} from '../routing/base-category-registry';
import {AllService} from '../all/all.service';
import {buildListingFilterParams} from '../shared/listing-filter.utils';
import {BrowseKeywordService} from '../shared/browse-keyword.service';
import {BrandThemeService} from '../shared/brand-theme.service';
import {formatBrowseSegmentLabel} from '../shared/browse-seo.utils';

interface BrowseCategoryLink {
  url: string;
  name: string;
}

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './header-slick.component.html',
  styleUrls: ['./header-slick.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() public sidenavToggle = new EventEmitter();
  url = '';
  imageSrc = '';
  selectCounty = 'All';
  counties: any;
  itemsStr = '';
  itemsStr2 = '';
  itemsName = '';
  category = '';
  subcategory = '';
  items: any;
  itemsTestTemp: any;
  itemsTest: any;
  itemsTemp: any = [];
  itemsList: any = [];
  private itemsShopCopy: any;
  currentUser: any;

  searchQuery = '';
  results: any[] = [];
  browseCategories: BrowseCategoryLink[] = [];
  showCategoryMenu = false;
  profileId: string | null = null;
  notificationCount = 0;
  addListingBreadcrumbs: { label: string; routeLink: string }[] = [];

  private categoryMenuCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private categoriesLoaded = false;
  private categoriesLoadKey = '';
  private themeSub?: Subscription;

  private readonly nonBrowseCategoryUrls = new Set([
    'home', 'add-listing', 'add-item', 'update-item', 'item', 'items',
    'signup', 'login', 'profile', 'account', 'admin', 'payment', 'messaging', 'compare'
  ]);

  brand = {
    name: 'Marketplace',
    accentColor: '#2563eb',
    logoUrl: ''
  };

  get brandInitial(): string {
    return (this.brand.name || 'M').trim().charAt(0).toUpperCase();
  }

  get logoSrc(): string {
    return this.brandThemeService.resolveMediaUrl(this.brand.logoUrl, this.imageSrc);
  }

  private searchTerms = new Subject<string>();

  constructor(private token: TokenStorageService,
              private homeService: HomeService,
              private allService: AllService,
              private browseKeywordService: BrowseKeywordService,
              private brandThemeService: BrandThemeService,
              private router: Router,
              private breadcrumbService: BreadcrumbService,
              public globals: Globals) {
    this.searchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        if (this.isOnListingBrowse()) {
          this.browseKeywordService.apply((term ?? '').trim());
          return of([]);
        }
        return this.fetchCategories(term);
      })
    ).subscribe(data => {
      this.results = (data || []).slice(0, 24);
    });
    environment.then(environment => {
        this.url = environment["backendURL"];
        this.imageSrc = environment["images"];
        if (environment["brand"]) {
          this.brand = {...this.brand, ...environment["brand"]};
        }
      }
    );
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.router.navigated = false;
        this.syncProfileFromUrl();
        this.syncAddListingBreadcrumbs();
      }
    });
    this.syncProfileFromUrl();
    this.syncAddListingBreadcrumbs();
  }

  private syncAddListingBreadcrumbs(): void {
    this.addListingBreadcrumbs = buildAddListingBreadcrumbTrail(this.router.url);
  }

  ngOnInit(): void {
    this.currentUser = this.token.getUser();
    this.themeSub = this.brandThemeService.theme$.subscribe(theme => {
      this.brand = {
        ...this.brand,
        name: theme.marketplaceName || this.brand.name,
        accentColor: theme.fontColor,
        logoUrl: theme.logoUrl || '',
      };
    });
    this.brandThemeService.ensureLoaded().subscribe(() => {
      if (this.profileId) {
        this.brandThemeService.applyProfileTheme(this.profileId).subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
  }

  private syncProfileFromUrl(): void {
    const segments = this.currentUrlSegments();
    const nextProfileId = resolveProfileId(segments);
    if (nextProfileId !== this.profileId) {
      this.profileId = nextProfileId;
      this.categoriesLoaded = false;
      this.browseCategories = [];
      this.clearSearch();
      if (this.showCategoryMenu) {
        this.loadBrowseCategories();
      }
      this.syncProfileTheme();
    } else {
      this.profileId = nextProfileId;
    }
  }

  private syncProfileTheme(): void {
    this.brandThemeService.ensureLoaded().subscribe(() => {
      if (this.profileId) {
        this.brandThemeService.applyProfileTheme(this.profileId).subscribe();
      } else {
        this.brandThemeService.clearProfileTheme();
      }
    });
  }

  private currentUrlSegments(): string[] {
    return this.router.url.split(/[?;#]/)[0].split('/').filter(Boolean);
  }

  goBack(): void {
    window.history.back();
  }

  buildResultRoute(item: any): any[] {
    if (item?.account_type && item?.url) {
      return ['/', item.url];
    }
    const bc = item?.bc;
    if (!bc) {
      return this.profileId ? ['/', this.profileId, item?.url] : ['/', item?.url];
    }
    const segments = [bc.c, bc.s, bc.ss, bc.sss, bc.ssss, bc.sssss, bc.ssssss, bc.sssssss, bc.ssssssss]
      .map((level: any) => level?.url)
      .filter((url: any) => !!url);
    return this.profileId
      ? ['/', this.profileId, ...segments, itemUrlSegment(item)]
      : ['/', ...segments, itemUrlSegment(item)];
  }

  buildCategoryRoute(category: BrowseCategoryLink): any[] {
    return this.profileId
      ? ['/', this.profileId, category.url]
      : ['/', category.url];
  }

  categoryLinkLabel(category: BrowseCategoryLink): string {
    const name = formatBrowseSegmentLabel(category.name || category.url).toUpperCase();
    return this.profileId ? `${this.profileId} / ${name}` : name;
  }

  /** Humanize auto-generated path crumbs (e.g. living-room → Living Room). */
  formatBreadcrumbLabel(label: string | null | undefined): string {
    const raw = `${label ?? ''}`.trim();
    if (!raw) {
      return '';
    }
    // Keep free-text labels (item titles, already spaced names).
    if (!raw.includes('-') || /\s/.test(raw)) {
      return raw;
    }
    return formatBrowseSegmentLabel(raw);
  }

  get categoryMenuTitle(): string {
    return this.profileId
      ? `${this.profileId} categories`
      : 'Browse categories';
  }

  get categoryMenuEmptyMessage(): string {
    return this.profileId
      ? `${this.profileId} has no listed categories yet.`
      : 'No categories available.';
  }

  get searchResultsTitle(): string {
    return this.profileId
      ? `Results in ${this.profileId}'s shop`
      : 'Results';
  }

  get searchPlaceholder(): string {
    if (this.profileId) {
      return `Search ${this.profileId}'s listings...`;
    }
    return 'Search listings...';
  }

  private isOnListingBrowse(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return /\/all\/?$/.test(path);
  }

  private buildHeaderSearchFilter(term: string): Record<string, unknown> {
    const filter: Record<string, unknown> = this.isOnListingBrowse()
      ? {...(this.globals.listingFilter as Record<string, unknown>)}
      : {};
    filter.keyword = term;
    filter.page = 0;
    filter.size = 24;
    if (this.profileId) {
      filter.profile = this.profileId;
    }
    return buildListingFilterParams(filter);
  }

  loadBrowseCategories(): void {
    if (!this.url) {
      return;
    }
    const loadKey = this.profileId || '__global__';
    if (this.categoriesLoaded && this.categoriesLoadKey === loadKey) {
      return;
    }

    if (this.profileId) {
      this.allService.getItemsCategories(this.url + '/items/categories', { profile: this.profileId })
        .subscribe({
          next: (rows) => {
            this.finishCategoryLoad(loadKey, this.normalizeBrowseCategories(rows, true));
          },
          error: (err) => console.error('Failed to load profile browse categories', err),
        });
      return;
    }

    this.homeService.getCategories(this.url).subscribe({
      next: (categories) => {
        this.finishCategoryLoad(loadKey, this.normalizeBrowseCategories(categories, false));
      },
      error: (err) => console.error('Failed to load browse categories', err),
    });
  }

  private finishCategoryLoad(loadKey: string, categories: BrowseCategoryLink[]): void {
    this.browseCategories = categories;
    this.categoriesLoadKey = loadKey;
    this.categoriesLoaded = true;
  }

  private normalizeBrowseCategories(rows: any[], profileScoped: boolean): BrowseCategoryLink[] {
    const seen = new Set<string>();
    const out: BrowseCategoryLink[] = [];

    for (const row of rows || []) {
      let url = '';
      let name = '';

      if (Array.isArray(row)) {
        url = String(row[1] ?? '').trim();
        name = url;
      } else {
        url = String(row?.url ?? '').trim();
        name = String(row?.name || url).trim();
        if (!profileScoped && !row?.module) {
          continue;
        }
      }

      if (!url) {
        continue;
      }

      const key = url.toLowerCase();
      if (seen.has(key) || this.nonBrowseCategoryUrls.has(key)) {
        continue;
      }

      if (profileScoped && !isKnownBaseCategory(url)) {
        continue;
      }

      seen.add(key);
      out.push({ url, name });
    }

    return out.sort((a, b) => a.name.localeCompare(b.name));
  }

  openCategoryMenu(): void {
    if (this.categoryMenuCloseTimer) {
      clearTimeout(this.categoryMenuCloseTimer);
      this.categoryMenuCloseTimer = null;
    }
    this.loadBrowseCategories();
    this.showCategoryMenu = true;
  }

  scheduleCloseCategoryMenu(): void {
    if (this.categoryMenuCloseTimer) {
      clearTimeout(this.categoryMenuCloseTimer);
    }
    this.categoryMenuCloseTimer = setTimeout(() => {
      this.showCategoryMenu = false;
      this.categoryMenuCloseTimer = null;
    }, 200);
  }

  cancelCloseCategoryMenu(): void {
    if (this.categoryMenuCloseTimer) {
      clearTimeout(this.categoryMenuCloseTimer);
      this.categoryMenuCloseTimer = null;
    }
  }

  closeCategoryMenu(): void {
    this.cancelCloseCategoryMenu();
    this.showCategoryMenu = false;
  }

  getCounties() {
    this.homeService.getCounties(this.url,'/counties')
      .subscribe(response => {
        this.counties = response;
      })
  }

  onSelected(value: string): void {
    this.selectCounty = value;
  }

  onSearchChange() {
    this.searchTerms.next(this.searchQuery);
  }

  onSearchSubmit(): void {
    const trimmed = this.searchQuery?.trim();
    if (!trimmed) {
      return;
    }
    if (this.isOnListingBrowse()) {
      this.browseKeywordService.apply(trimmed);
      this.results = [];
      return;
    }
    this.fetchCategories(trimmed).subscribe(items => {
      this.results = (items || []).slice(0, 24);
    });
  }

  fetchCategories(term: string): Observable<any[]> {
    const trimmed = term?.trim();
    if (!trimmed || !this.url) {
      return of([]);
    }

    const listingSearch$ = this.allService.getWithDataHeader(
      `${this.url}/api/listings/search`,
      this.buildHeaderSearchFilter(trimmed)
    ).pipe(
      map(response => (response?.items ?? []).map((row: any) => row.item ?? row)),
      catchError(() => of([]))
    );

    if (this.profileId) {
      return listingSearch$;
    }

    const profileSearch$ = this.homeService.getItemsByName(
      this.url + '/items/',
      trimmed,
      this.selectCounty
    ).pipe(
      map(rows => this.extractProfileResults(rows)),
      catchError(() => of([]))
    );

    return forkJoin([listingSearch$, profileSearch$]).pipe(
      map(([listings, profiles]) => this.mergeSearchResults(listings, profiles))
    );
  }

  private extractProfileResults(rows: any[]): any[] {
    return (rows || []).filter(row => row?.account_type && row?.url);
  }

  private mergeSearchResults(listings: any[], profiles: any[]): any[] {
    const seen = new Set<string>();
    const merged: any[] = [];
    const add = (row: any) => {
      const key = row?.itemId ?? row?.url ?? row?.name;
      if (!key || seen.has(String(key))) {
        return;
      }
      seen.add(String(key));
      merged.push(row);
    };
    listings.forEach(add);
    profiles.forEach(add);
    return merged;
  }

  public onToggleSidenav = () => {
    this.sidenavToggle.emit();
  }

  redirectHome() {
    let data = {};
    if (this.itemsStr.length > 3) {
      if (this.itemsStr.includes(' in ')) {
        let split = this.itemsStr.split(' in ');
        let splitTwo = split[1].split(' - ');
        data = {
          item: split[0],
          location: this.selectCounty,
          subcategory: splitTwo[0],
          category: splitTwo[1]
        };
      } else {
        data = {
          item: this.itemsStr2,
          location: this.selectCounty
        };
      }

      this.homeService.getSearchHeaderItem(this.url + '/items/', data)
        .subscribe(response => {
          this.itemsShopCopy = response;
          this.router.navigate(['item'], {state: {search_items: this.itemsShopCopy}});
        });
    }
  }

  logout() {
    this.globals.listingFilter.profile = null;
    this.globals.listingFilter.category = null;
    this.globals.listingFilter.subcategory = null;
    this.globals.listingFilter.subSubcategory = null;
    this.globals.listingFilter.subSubSubcategory = null;
    this.globals.listingFilter.subSubSubSubcategory = null;
    this.homeService.signout(this.url).subscribe(
      data => {
        this.token.signOut();
        this.currentUser = null;
        this.router.navigate(['/home']);
      },
      err => {
        console.log(err.error.message);
      });
  }

  protected readonly environment = environment;

  generateLink(item: any) {
    return 'test';
  }

  clearSearch() {
    this.searchQuery = '';
    this.results = [];
    if (this.isOnListingBrowse()) {
      this.browseKeywordService.apply('');
    }
  }

  onSearchFocus() {
  }

  onSearchBlur(event: any) {
    setTimeout(() => {
      if (!event.relatedTarget || !event.relatedTarget.closest('.search-dropdown')) {
      }
    }, 200);
  }

}
